import React, { useState, useEffect, ReactElement } from 'react';
import axiosExternal from 'axios';
import ReactHtmlParser, { DOMNode, Element, domToReact } from 'html-react-parser';
import { Box, Checkbox, FormControlLabel, Link, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { checkUserRegion, getExperienceInitCookie } from '../../common/utils/experienceInitCookie';
import { ENV } from '../../constants/envConstant';
import { Link as BrowserLink } from 'react-router-dom';
import { routeMappings } from '../../constants/routeMappings';
import { IELink } from '../../common/components/IELink';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import { simplifiedAuthStyles } from '../styles/simplifiedAuth';
import { WCAGStrandardStyles } from '../../common/styles/WCAGStandardStyles';
import { TemplateLayout } from '../../store/interfaces';
import { getCookie, getCurrentLocale } from '../../common/utils/cookies';
import { apiUrls } from '../../constants';
import { DPNItem, DPNConsentType } from '../../dpn/interfaces';

interface DpnCheckboxProps {
  layoutCode: TemplateLayout;
  dpnAcceptance: string;
  dpnAcceptanceHelperText: string;
  dataTestId?: string;
  handleDpnAcceptance: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DpnCheckbox = ({
  layoutCode,
  dpnAcceptance,
  dpnAcceptanceHelperText,
  dataTestId,
  handleDpnAcceptance,
}: DpnCheckboxProps): React.JSX.Element => {
  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  const { classes } = simplifiedAuthStyles({ layoutCode });
  const { classes: WCAGClasses } = WCAGStrandardStyles();

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dpnOptions, setDpnOptions] = useState<DPNItem[] | undefined>(undefined);

  const replace = (node: DOMNode, index: number): ReactElement | undefined => {
    if (node.type === 'tag' && node.name === 'a' && node.attribs && node.attribs['data-link'] === 'internal') {
      return (
        <Link key={index} className={classes.fontSize14} component={BrowserLink} to={node.attribs.href}>
          <IELink>{domToReact(node.children as DOMNode[])}</IELink>
        </Link>
      );
    }
  };

  useEffect(() => {
    const getCustomDpnOptions = async (): Promise<void> => {
      try {
        const requiredEntities = ['dataProtectionNoticeMetaData'];
        const expCookie = getExperienceInitCookie();
        const companyUserId = expCookie?.companyUserId;
        const langCode = getCurrentLocale().replace(/_/g, '-');
        const params = {
          companyUserId,
          langCode,
          requiredEntities,
        };
        const { data: responseData } = await axiosExternal.get(apiUrls.TC_PLUS_GET_DPN, { params });
        if (responseData.data && responseData.data.dataProtectionNoticeMetaData) {
          setDpnOptions(
            responseData.data.dataProtectionNoticeMetaData?.sort(
              (item1: DPNItem, item2: DPNItem) => item1.sequence - item2.sequence,
            ),
          );
        }
      } catch (e) {
        console.error('Error in getting dpnDetails data:', e);
      }
    };
    (async (): Promise<void> => {
      if (tcPlusFlow) {
        await getCustomDpnOptions();
      }
      setIsLoading(false);
    })();
  }, [tcPlusFlow]);

  return isLoading ? (
    <Box className={classes.marginBottom15}>
      <Skeleton animation="wave" variant="text" />
      <Skeleton animation="wave" variant="text" width="40%" />
    </Box>
  ) : (
    <Box className={`${classes.marginBottom15} ${classes.dpnLabel}`}>
      {dpnOptions ? (
        dpnOptions?.map((dpnOptionItem) => {
          if (dpnOptionItem.type === DPNConsentType.CHECKBOX) {
            return (
              <FormControlLabel
                key={dpnOptionItem.sequence}
                className={classes.dpnLabelItem}
                control={
                  <Checkbox
                    color="primary"
                    onChange={handleDpnAcceptance}
                    name="dpnAcceptance"
                    data-testid={dataTestId}
                    checked={dpnAcceptance === 'true'}
                  />
                }
                label={
                  <Typography className={WCAGClasses.subtitle1}>
                    {ReactHtmlParser(dpnOptionItem.text, { replace })}
                  </Typography>
                }
              />
            );
          } else if (dpnOptionItem.type === DPNConsentType.INFO) {
            return (
              <Box className={`${WCAGClasses.subtitle1} ${classes.dpnLabelItem}`} key={dpnOptionItem.sequence}>
                {ReactHtmlParser(dpnOptionItem.text, { replace })}
              </Box>
            );
          }
          return <></>;
        })
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              onChange={handleDpnAcceptance}
              name="dpnAcceptance"
              data-testid={dataTestId}
              checked={dpnAcceptance === 'true'}
            />
          }
          label={
            <Typography className={WCAGClasses.subtitle1}>
              {checkUserRegion(ENV.CN) ? t('cnRegionDpn') : t('readAndAgreeToSHLsDPN')}&nbsp;
              <Link
                className={classes.fontSize14}
                component={BrowserLink}
                to={{
                  pathname: routeMappings['DPN'],
                  search: '?from=staticLink',
                }}>
                <IELink>{t('dataProtectionNotice')}</IELink>
              </Link>
            </Typography>
          }
        />
      )}
      {/* <FormHelperText error={dpnAcceptanceHelperText !== ''}> */}
      {dpnAcceptanceHelperText !== '' ? (
        <span className={classes.helperText}>
          <ErrorOutlineOutlined />
          &nbsp;
          {t(`${dpnAcceptanceHelperText}`)}
        </span>
      ) : undefined}
      {/* </FormHelperText> */}
    </Box>
  );
};
